// src/auditLogger.js
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";

/**
 * Audit Logger Utility
 * Logs user actions for audit trail purposes
 */

export const AUDIT_ACTIONS = {
  // Authentication
  LOGIN: "login",
  LOGOUT: "logout",
  REGISTER: "register",
  
  // Project/Tender actions
  PROJECT_CREATE: "project_create",
  PROJECT_EDIT: "project_edit",
  PROJECT_DELETE: "project_delete",
  PROJECT_ARCHIVE: "project_archive",
  PROJECT_UNARCHIVE: "project_unarchive",
  PROJECT_STATUS_CHANGE: "project_status_change",
  
  // File actions
  FILE_UPLOAD: "file_upload",
  FILE_DELETE: "file_delete",
  
  // Costing actions
  COSTING_EDIT: "costing_edit",
  COSTING_ITEM_ADD: "costing_item_add",
  COSTING_ITEM_DELETE: "costing_item_delete",
  
  // Clause/Review actions
  CLAUSE_ADD: "clause_add",
  CLAUSE_EDIT: "clause_edit",
  CLAUSE_DELETE: "clause_delete",
  CLAUSE_STATUS_CHANGE: "clause_status_change",
  
  // User management
  USER_ROLE_CHANGE: "user_role_change",
  USER_STATUS_CHANGE: "user_status_change",
  USER_DELETE: "user_delete",
  
  // Vendor actions
  VENDOR_CREATE: "vendor_create",
  VENDOR_EDIT: "vendor_edit",
  VENDOR_DELETE: "vendor_delete",
};

/**
 * Log an audit event
 * @param {Object} params - Audit log parameters
 * @param {string} params.action - Action type from AUDIT_ACTIONS
 * @param {Object} params.user - User who performed the action
 * @param {string} params.user.uid - User ID
 * @param {string} params.user.email - User email
 * @param {string} params.user.displayName - User display name
 * @param {string} params.user.organizationId - Organization ID (optional)
 * @param {string} params.targetType - Type of target (tender, user, vendor, etc.)
 * @param {string} params.targetId - ID of the target resource
 * @param {string} params.targetName - Name of the target resource
 * @param {Object} params.changes - Object describing what changed (optional)
 * @param {string} params.details - Additional details (optional)
 */
export async function logAuditEvent({
  action,
  user,
  targetType = null,
  targetId = null,
  targetName = null,
  changes = null,
  details = null,
}) {
  try {
    // Don't log if no user (shouldn't happen in protected routes)
    if (!user || !user.uid) {
      console.warn("Audit log: No user provided");
      return;
    }

    const auditEntry = {
      action,
      userId: user.uid,
      userEmail: user.email || "unknown",
      userDisplayName: user.displayName || user.email || "Unknown User",
      organizationId: user.organizationId || null, // Add organization ID for data isolation
      timestamp: serverTimestamp(),
      targetType,
      targetId,
      targetName,
      changes,
      details,
      userAgent: navigator.userAgent,
      // TODO: IP address tracking can be implemented via a backend API endpoint
      // that captures the client IP from request headers for compliance requirements.
      // Consider privacy regulations (GDPR, CCPA) before implementing.
      ipAddress: null,
    };

    await addDoc(collection(db, "auditLogs"), auditEntry);
  } catch (error) {
    // Fail silently to not interrupt user operations
    console.error("Error logging audit event:", error);
  }
}

/**
 * Convenience function to log project-related actions
 */
export async function logProjectAction(action, user, projectId, projectName, changes = null, details = null) {
  return logAuditEvent({
    action,
    user,
    targetType: "project",
    targetId: projectId,
    targetName: projectName,
    changes,
    details,
  });
}

/**
 * Convenience function to log user management actions
 */
export async function logUserAction(action, user, targetUserId, targetUserName, changes = null, details = null) {
  return logAuditEvent({
    action,
    user,
    targetType: "user",
    targetId: targetUserId,
    targetName: targetUserName,
    changes,
    details,
  });
}

/**
 * Convenience function to log authentication actions
 */
export async function logAuthAction(action, user, details = null) {
  return logAuditEvent({
    action,
    user,
    targetType: "auth",
    targetId: user.uid,
    targetName: user.email,
    changes: null,
    details,
  });
}
